import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import type { Event } from '@/types/event';
import { imageSrc } from '@/utils/event';
import { Calendar, MapPin, AlertCircle } from 'lucide-react';
import { Modal } from '../basic/Modal';
import { Card } from '../basic/Card';
import { Button } from '../basic/Button';
import { eventApi } from '@/services/eventsApi';
import { useAuth } from '@/contexts/UserContext';
import ModalHeader from './ModalHeader';

interface ReservationModalProps {
  event: Event;
  onConfirm: (nextId: string | number, phoneNumber?: string) => Promise<any>;
  onClose: () => void;
  quota?: any;
}

const ReservationModal: React.FC<ReservationModalProps> = ({ event, onConfirm, onClose, quota = {} }) => {
  const { isAuthenticated } = useAuth();
  const [myQuotas, setMyQuotasStatus] = useState<Record<string, any>>(quota);
  
  // console.log('ReservationModal:',quota, myQuotas);
  // const eventType = event.is_networking_event || event.type == "prof networking" ? 'professional_network_building' : 'career_coaching';
  const eventType = event.type == "prof networking" ? 'professional_network_building' : 'career_coaching';
  const balanceQuotas = myQuotas?.summary && eventType in myQuotas?.summary ? myQuotas?.summary[eventType]?.total_remaining : 0;
  const canReserve = balanceQuotas > 0;
  const simulateQuotas = canReserve ? balanceQuotas - 1 : 0;
  const scheduleId = event?.next_schedule?.id;
  const [phoneNumber, setPhoneNumber] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  // console.log('ReservationModal:',eventType, balanceQuotas, canReserve, simulateQuotas, scheduleId, myQuotas?.summary[eventType]?.total_remaining);

  // const onReserve = async (e: React.FormEvent) => {
  //   console.log(event);
  //   e.preventDefault();
  //   setIsLoading(true);
    
  //   try {
  //     await onConfirm(event.id, phoneNumber || undefined);
  //     // Success modal will be shown by parent component
  //   } catch (error) {
  //     // Error is already handled in the parent component
  //   } finally {
  //     setIsLoading(false);
  //   }
  // };

  const onReserve = () => {
    // console.log(event);
    setIsLoading(true);
    // const scheduleId = event?.next_schedule?.id;

    if (scheduleId) {
      toast.info('Registering...', {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
      onConfirm(scheduleId, phoneNumber || undefined);
    } else {
      // toast.error('Next schedule not available, please contact orgenize for further details...', {
      //   position: "top-right",
      //   autoClose: 5000,
      //   hideProgressBar: false,
      //   closeOnClick: true,
      //   pauseOnHover: true,
      //   draggable: true,
      // });
      onClose();
    }
    setIsLoading(false);
  };
  
  useEffect(() => {
    if (isAuthenticated) {
      const fetchMyQuotas = async () => {
        try {
          // console.log('trying to fetch my quotas');
          const response = await eventApi.getMyQuotas();
          const quotas = (response as any)?.data || response;
          // console.log(quotas);
          setMyQuotasStatus(quotas);
        } catch (error) {
          console.error('Error fetching my quotas:', error);
        }
      };
      fetchMyQuotas();
    }
  }, [isAuthenticated]);

  const isFeatured = event.is_featured || false;
  const tag = event.taggings?.[0]?.name || null;
  const category = tag || event.category as string || null;
  const type = tag || category || event.type || null;

  return (
    <Modal onClose={onClose} size="sm">
      <div className="px-1 sm:px-2 pt-1 sm:pt-2">
        <ModalHeader event={event} onClose={onClose} />
      </div>
      <div className="px-4 sm:px-5 lg:px-6 pb-4 sm:pb-5 lg:pb-6">
        {/* Header */}
        <div className="text-center mb-6 sm:mb-8">
          <div className="w-12 h-12 sm:w-16 sm:h-16 mx-auto mb-3 sm:mb-4 bg-orange-500/20 rounded-full flex items-center justify-center">
            <AlertCircle size={24} className="sm:w-8 sm:h-8 text-amber-500" />
          </div>
          <h2 className="text-xl sm:text-2xl lg:text-3xl font-normal text-white mb-1 sm:mb-2">
            Confirm Your Enrollment
          </h2>
          <p className="text-white/70 text-sm sm:text-base font-normal px-2">
            Please review the details before confirming
          </p>
        </div>

        {/* Event Details Card */}
        <Card className="p-4 sm:p-5 lg:p-6 mb-4 sm:mb-5 lg:mb-6 text-left">
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 mb-3 sm:mb-4 justify-center sm:justify-start items-center sm:items-start">
            <img 
              className="w-20 h-20 sm:w-24 sm:h-24 rounded-xl sm:rounded-2xl object-cover flex-shrink-0" 
              src={imageSrc(event)} 
              alt={event.title}
            />
            <div className="flex-1 text-center sm:text-left">
              {isFeatured && <span className="uppercase inline-block bg-orange-500/20 text-amber-500 text-xs px-2 sm:px-3 py-1 rounded-full mb-1">
                Featured
              </span>}
              <h3 className="text-lg sm:text-xl font-normal text-white mb-0.5 line-clamp-2">
                {event.title}
              </h3>
              {(category) && <p className="text-white/60 text-xs sm:text-sm line-clamp-1">
                {category}
              </p>}
            </div>
          </div>
          
          <div className="border-t border-white/10 pt-3 sm:pt-4 space-y-2 sm:space-y-3">
            <div className="flex items-center gap-2 sm:gap-3">
              <Calendar size={16} className="sm:w-5 sm:h-5 text-amber-500 flex-shrink-0" />
              <span className="text-white text-sm sm:text-base font-light break-words">
                {new Date(event.event_date as string).toLocaleDateString('en-US', { 
                  year: 'numeric', 
                  month: 'short', 
                  day: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit'
                })}
              </span>
            </div>
            <div className="flex items-center gap-2 sm:gap-3">
              <MapPin size={16} className="sm:w-5 sm:h-5 text-amber-500 flex-shrink-0" />
              <span className="text-white text-sm sm:text-base font-light break-words">
                {event.address?.venue || event.location}
              </span>
            </div>
          </div>
        </Card>

        {/* Subscription Status */}
        <Card variant="gradient" className="p-4 sm:p-5 lg:p-6 mb-4 sm:mb-5 lg:mb-6">
          <div className="flex items-center gap-3 sm:gap-4 mb-3 sm:mb-4">
            <div className="w-10 h-10 sm:w-12 sm:h-12 lg:w-14 lg:h-14 bg-orange-500/20 rounded-full flex items-center justify-center flex-shrink-0">
              <img 
                className="w-6 h-6 sm:w-8 sm:h-8 object-cover" 
                src="/favicon/android-chrome-512x512.png" 
                alt="Subscription Status"
              />
            </div>
            <div className="text-start min-w-0">
              <h4 className="text-white font-normal text-sm sm:text-base mb-0.5">Subscription Status</h4>
              <p className="text-white/70 text-xs sm:text-sm break-words">
                You are currently on the <span className="text-white font-normal">Skillsture Plus</span> plan
              </p>
            </div>
          </div>

          <div className="bg-orange-500/10 rounded-xl sm:rounded-2xl p-3 sm:p-4 border border-white/20">
            <div className="flex flex-col sm:flex-row justify-between items-center gap-3 sm:gap-4">
              <div className="text-center sm:text-left">
                <p className="text-orange-100 text-xs sm:text-sm font-light mb-0.5">Remaining Event Quotas</p>
                <p className="text-white text-xl sm:text-2xl font-semibold">{balanceQuotas} quota</p>
              </div>
              <div className="text-center sm:text-right">
                <p className="text-orange-100 text-xs sm:text-sm font-light mb-0.5">After Enrollment</p>
                <p className="text-white text-xl sm:text-2xl font-semibold">{simulateQuotas} quota</p>
              </div>
            </div>
          </div>
        </Card>

        {/* Action Buttons */}
        <div className="space-y-2 sm:space-y-3">
          <Button
            onClick={onReserve}
            variant="primary"
            size="lg"
            className="w-full text-sm sm:text-base"
            disabled={!canReserve || isLoading}
          >
            {isLoading ? (
              <>
                <div className="w-3 h-3 sm:w-4 sm:h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                Registering...
              </>
            ) : (
              'Confirm Registration'
            )}
          </Button>
          <Button
            onClick={onClose}
            variant="outline"
            size="lg"
            className="w-full text-sm sm:text-base"
            disabled={isLoading}
          >
            Cancel
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default ReservationModal;